import { AssetUpload } from '../hooks/usePhotoUpload';
import { AssetSource, AssetType } from '../types/photo';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface AssetValidationError {
  field: string;
  message: string;
  assetId?: string;
}

export class AssetValidationService {
  static readonly REQUIRED_FIELDS: (keyof AssetUpload)[] = ['id', 'title'];
  static readonly VALID_SOURCES: AssetSource[] = ['Impact', 'Brandfolder', 'Local'];
  static readonly VALID_TYPES: AssetType[] = ['Image', 'Video'];
  static readonly MAX_TITLE_LENGTH = 255;
  static readonly MAX_DESCRIPTION_LENGTH = 1000;
  static readonly MAX_ASSETS_BATCH = 5;

  /**
   * Validates a single asset upload object
   */
  static validateAsset(asset: AssetUpload): AssetValidationError[] {
    const errors: AssetValidationError[] = [];

    // Check required fields
    AssetValidationService.REQUIRED_FIELDS.forEach(field => {
      if (!asset[field] || (typeof asset[field] === 'string' && asset[field].trim().length === 0)) {
        errors.push({
          field,
          message: `${field} is required and cannot be empty`,
          assetId: asset.id
        });
      }
    });

    // Validate ID format (should be a valid string identifier)
    if (asset.id && !AssetValidationService.isValidId(asset.id)) {
      errors.push({
        field: 'id',
        message: 'Asset ID contains invalid characters',
        assetId: asset.id
      });
    }

    // Validate title length
    if (asset.title && asset.title.length > AssetValidationService.MAX_TITLE_LENGTH) {
      errors.push({
        field: 'title',
        message: `Title cannot exceed ${AssetValidationService.MAX_TITLE_LENGTH} characters`,
        assetId: asset.id
      });
    }

    // Validate description length
    if (asset.description && asset.description.length > AssetValidationService.MAX_DESCRIPTION_LENGTH) {
      errors.push({
        field: 'description',
        message: `Description cannot exceed ${AssetValidationService.MAX_DESCRIPTION_LENGTH} characters`,
        assetId: asset.id
      });
    }

    // Validate source enum
    if (asset.source && !AssetValidationService.VALID_SOURCES.includes(asset.source)) {
      errors.push({
        field: 'source',
        message: `Source must be one of: ${AssetValidationService.VALID_SOURCES.join(', ')}`,
        assetId: asset.id
      });
    }

    // Validate type enum
    if (asset.type && !AssetValidationService.VALID_TYPES.includes(asset.type)) {
      errors.push({
        field: 'type',
        message: `Type must be one of: ${AssetValidationService.VALID_TYPES.join(', ')}`,
        assetId: asset.id
      });
    }

    return errors;
  }

  /**
   * Validates a batch of asset uploads
   */
  static validateAssetBatch(assets: AssetUpload[]): ValidationResult {
    const errors: string[] = [];
    const assetErrors: AssetValidationError[] = [];

    // Check batch size
    if (assets.length === 0) {
      errors.push('Assets array cannot be empty');
    }

    if (assets.length > AssetValidationService.MAX_ASSETS_BATCH) {
      errors.push(`Cannot upload more than ${AssetValidationService.MAX_ASSETS_BATCH} assets at once`);
    }

    // Check for duplicate IDs
    const ids = assets.map(asset => asset.id).filter(Boolean);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      errors.push('Duplicate asset IDs found in batch');
    }

    // Validate each asset
    assets.forEach(asset => {
      const assetValidationErrors = AssetValidationService.validateAsset(asset);
      assetErrors.push(...assetValidationErrors);
    });

    // Convert asset-specific errors to general errors
    assetErrors.forEach(error => {
      const errorMessage = error.assetId 
        ? `Asset ${error.assetId}: ${error.message}`
        : error.message;
      errors.push(errorMessage);
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validates asset upload request structure
   */
  static validateUploadRequest(requestBody: any): ValidationResult {
    const errors: string[] = [];

    // Check if assets property exists
    if (!requestBody.assets) {
      errors.push('assets field is required');
      return { isValid: false, errors };
    }

    // Check if assets is an array
    if (!Array.isArray(requestBody.assets)) {
      errors.push('assets must be an array');
      return { isValid: false, errors };
    }

    // Validate the asset batch
    try {
      const batchValidation = AssetValidationService.validateAssetBatch(requestBody.assets);
      errors.push(...batchValidation.errors);
    } catch (error) {
      errors.push('Invalid asset data structure');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Sanitizes and normalizes an asset upload object
   */
  static normalizeAsset(asset: AssetUpload): AssetUpload {
    const normalized: AssetUpload = {
      id: asset.id?.trim() || '',
      title: asset.title?.trim() || '',
      description: asset.description?.trim() || '',
      source: asset.source || 'Impact',
      type: asset.type || 'Image'
    };

    // Remove empty description if not provided
    if (!normalized.description) {
      delete normalized.description;
    }

    // Preserve Brandfolder-specific fields
    if (asset.village_id) normalized.village_id = asset.village_id;
    if (asset.program) normalized.program = asset.program;
    if (asset.asset_type) normalized.asset_type = asset.asset_type;
    if (asset.event) normalized.event = asset.event;
    if (asset.date) normalized.date = asset.date;
    // Local-source fields produced by staging-upload
    if (asset.fileUrl) normalized.fileUrl = asset.fileUrl;
    if (asset.filename) normalized.filename = asset.filename;

    return normalized;
  }

  /**
   * Normalizes a batch of assets
   */
  static normalizeAssetBatch(assets: AssetUpload[]): AssetUpload[] {
    return assets.map(asset => AssetValidationService.normalizeAsset(asset));
  }

  /**
   * Checks if an asset ID is valid (basic alphanumeric with underscores and hyphens)
   */
  private static isValidId(id: string): boolean {
    const validIdPattern = /^[a-zA-Z0-9_-]+$/;
    return validIdPattern.test(id) && id.length > 0 && id.length <= 100;
  }

  /**
   * Gets validation rules summary for display to users
   */
  static getValidationRules(): Record<string, string> {
    return {
      'Asset ID': 'Required, alphanumeric with underscores/hyphens, max 100 characters',
      'Title': `Required, max ${AssetValidationService.MAX_TITLE_LENGTH} characters`,
      'Description': `Optional, max ${AssetValidationService.MAX_DESCRIPTION_LENGTH} characters`,
      'Source': `Optional, must be one of: ${AssetValidationService.VALID_SOURCES.join(', ')} (default: Impact)`,
      'Type': `Optional, must be one of: ${AssetValidationService.VALID_TYPES.join(', ')} (default: Image)`,
      'Batch Size': `Max ${AssetValidationService.MAX_ASSETS_BATCH} assets per upload`
    };
  }
}

// Export convenience functions
export const validateAsset = AssetValidationService.validateAsset;
export const validateAssetBatch = AssetValidationService.validateAssetBatch;
export const validateUploadRequest = AssetValidationService.validateUploadRequest;
export const normalizeAsset = AssetValidationService.normalizeAsset;
export const normalizeAssetBatch = AssetValidationService.normalizeAssetBatch;
export const getValidationRules = AssetValidationService.getValidationRules;